import { useAtom } from '@reatom/npm-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled, { css } from 'styled-components'

import { tradeOrderTypeAtom } from '@root/features/trade/trading'
import { isTauriOrBrowserLocalAtom } from '@root/shared'
import { UnderlineTabs } from '@root/shared/ui-kit'

import type { TradeType } from '@root/entities/trade-type'
import type { ChildrenProps } from '@root/shared/react'

type Props = ChildrenProps

/**
 * Market and Limit, with Limit unavailable on the web and saying so.
 *
 * It used to open the download dialog here, from the days when trading was
 * the desktop app's alone. That dialog is dead in both deployed builds -
 * VITE_DOWNLOAD_URL is unset, so its Download button opens `undefined`, and
 * its Open in the App button deep-links to a desktop build nobody publishes
 * for kol.trade - so pressing Limit offered a reader two broken doors and one
 * that closed the window.
 *
 * It is not replaced with a working tab, because the form behind it does not
 * work either: creating an order from this screen still calls deluge's
 * prepare/sign endpoints, and `tradeLimitOrderPrepareInstructionsCreate` is
 * not in the generated client at all. Showing the form would trade a useless
 * dialog for a crash. The watcher that fires orders is built and tested; it
 * is only the placing that was never ported, and the bot can still do it.
 *
 * So the tab is disabled and the reason appears when somebody asks for it by
 * pressing it. A permanent line under the tabs would be noise for everybody
 * who never wanted a limit order.
 */
export const TradingHeadingTabs: React.FC<Props> = ({ children }) => {
  const [tradeOrderType, setTradeOrderType] = useAtom(tradeOrderTypeAtom)
  const { t } = useTranslation()
  const [isTauriOrBrowserLocal] = useAtom(isTauriOrBrowserLocalAtom)
  const [asked, setAsked] = useState(false)

  return (
    <>
      <UnderlineTabs
        value={tradeOrderType}
        onChange={setTradeOrderType}
        css={css`
          padding-inline: 10px;
          margin-bottom: 10px;
          grid-template-columns: auto auto 1fr;
          --tab-padding-inline: 8px;

          // Smaller height for tabs
          --tab-height: 35px;
        `}
      >
        <UnderlineTabs.Tab value={'market' satisfies TradeType.OrderType}>
          {t('trading.order.market')}
        </UnderlineTabs.Tab>
        <UnderlineTabs.Tab
          value={'limit' satisfies TradeType.OrderType}
          disabled={!isTauriOrBrowserLocal}
          // A handler is what stops the tab changing: `disabled` on this
          // component only sets `aria-disabled` and the styling, and the
          // press still goes through to onChange without one.
          onClick={isTauriOrBrowserLocal ? undefined : () => setAsked(true)}
        >
          {t('trading.order.limit')}
        </UnderlineTabs.Tab>
        {children}
      </UnderlineTabs>
      {asked ? <Note>{t('trading.order.limit-in-bot')}</Note> : undefined}
    </>
  )
}

const Note = styled.p`
  margin: -4px 0 10px;
  padding-inline: 10px;
  color: var(--text-10);
  font-size: 12px;
  line-height: 1.4;
`
